import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import type { IconName } from "@/components/ui";

export interface AiTour {
  id: string;
  title: string;
  subtitle: string;
  /** Etiqueta de grupo legible por módulo (p.ej. "Clientes", "Documentos"). */
  category: string;
  /** Guías del núcleo del producto. Se muestran primero en el lanzador. */
  core?: boolean;
  icon: IconName;
  steps: number;
  durationMin: number;
  matchPath?: RegExp;
  navigateTo?: string;
  build: () => DriveStep[];
}

/**
 * Orden de las categorías del núcleo. El lanzador (HelpButton) y la página
 * /help renderizan estos grupos primero y en este orden; el resto de
 * categorías se listan después en orden alfabético.
 */
export const CORE_CATEGORY_ORDER: readonly string[] = [
  "Clientes",
  "Expedientes",
  "Contrapartes",
  "Documentos",
  "Plantillas",
  "Chat IA",
];

/** Devuelve las tours agrupadas por categoría, con las CORE primero. */
export function groupTours(tours: AiTour[]): {
  category: string;
  core: boolean;
  tours: AiTour[];
}[] {
  const byCategory = new Map<string, AiTour[]>();
  for (const tour of tours) {
    const list = byCategory.get(tour.category);
    if (list) list.push(tour);
    else byCategory.set(tour.category, [tour]);
  }
  const restCategories = [...byCategory.keys()]
    .filter((category) => !CORE_CATEGORY_ORDER.includes(category))
    .sort((a, b) => a.localeCompare(b, "es"));
  const ordered = [
    ...CORE_CATEGORY_ORDER.filter((category) => byCategory.has(category)),
    ...restCategories,
  ];
  return ordered.map((category) => ({
    category,
    core: CORE_CATEGORY_ORDER.includes(category),
    tours: byCategory.get(category) ?? [],
  }));
}

const fallbackStep = (title: string, description: string): DriveStep => ({
  element: '[data-tour-id="help-button"]',
  popover: { title, description },
});

const HELP_BUTTON_SELECTOR = '[data-tour-id="help-button"]';

function hasVisibleTourTarget(element: DriveStep["element"]): boolean {
  if (!element || typeof document === "undefined") return true;
  const target =
    typeof element === "string"
      ? document.querySelector(element)
      : typeof element === "function"
        ? element()
        : element;
  const rect = target?.getBoundingClientRect();
  return Boolean(rect && rect.width > 0 && rect.height > 0);
}

function safeTourStep(step: DriveStep): DriveStep {
  if (hasVisibleTourTarget(step.element)) return step;
  if (hasVisibleTourTarget(HELP_BUTTON_SELECTOR)) {
    return { ...step, element: HELP_BUTTON_SELECTOR };
  }
  const safeStep = { ...step };
  delete safeStep.element;
  return safeStep;
}

export const TOURS: AiTour[] = [
  {
    id: "clients-basics",
    title: "Gestionar clientes",
    subtitle: "Alta, búsqueda, equipo y matters",
    category: "Clientes",
    core: true,
    icon: "building",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/clients/,
    navigateTo: "/clients",
    build: () => [
      {
        element: '[data-tour-id="clients-new-button"]',
        popover: {
          title: "Nuevo cliente",
          description:
            "El cliente es la entidad de primera clase: desde su ficha se abren matters, partes relacionadas y murallas de conflicto. Crea uno con razón social, RUC y sector.",
        },
      },
      {
        element: '[data-tour-id="clients-search"]',
        popover: {
          title: "Buscar y filtrar",
          description:
            "Filtra la lista por razón social, RUC o sector. Solo ves los clientes a los que tienes acceso según tu rol.",
        },
      },
      {
        element: '[data-tour-id="clients-table"]',
        popover: {
          title: "Lista de clientes",
          description:
            "Cada fila muestra estado y última actualización. Haz clic en un cliente para ver sus matters asociados.",
        },
      },
      {
        element: '[data-tour-id="clients-table"]',
        popover: {
          title: "Equipo y acceso",
          description:
            "Desde el menú de acciones de cada fila puedes abrir Equipo y compartir el cliente con responsables y colaboradores (ACL por recurso).",
        },
      },
      fallbackStep(
        "Del cliente al expediente",
        "Al crear un cliente o hacer clic en uno, Aequia te lleva a sus matters filtrados para abrir el primer expediente.",
      ),
    ],
  },
  {
    id: "ai-chat-basics",
    title: "Cómo usar el asistente IA",
    subtitle: "Conversaciones, historial y citas",
    category: "Chat IA",
    core: true,
    icon: "spark",
    steps: 7,
    durationMin: 2,
    matchPath: /^\/ai/,
    navigateTo: "/ai",
    build: () => [
      {
        element: '[data-tour-id="ai-thread-sidebar"]',
        popover: {
          title: "Tus conversaciones",
          description:
            "Cada conversación queda persistida. Puedes renombrar, archivar o retomar desde aquí.",
        },
      },
      {
        element: '[data-tour-id="ai-new-conversation"]',
        popover: {
          title: "Nueva conversación",
          description:
            "Empieza una conversación limpia sin perder el historial anterior.",
        },
      },
      {
        element: '[data-tour-id="ai-input"]',
        popover: {
          title: "Pregunta libre",
          description:
            "Escribe preguntas, instrucciones de redacción o análisis. Usa Cmd/Ctrl + Enter para enviar.",
        },
      },
      {
        element: '[data-tour-id="ai-scope-picker"]',
        popover: {
          title: "Scope RAG",
          description:
            "Elige si la IA debe responder con este documento, un proyecto, una contraparte, todo el tenant o fuentes globales.",
        },
      },
      {
        element: '[data-tour-id="ai-model-picker"]',
        popover: {
          title: "Elige el modelo IA",
          description:
            "Selecciona qué modelo responde (rápido, calidad o long-context). La preferencia se recuerda y cada respuesta muestra el modelo y costo cuando el backend devuelve esa telemetría.",
        },
      },
      {
        element: '[data-tour-id="ai-citation"]',
        popover: {
          title: "Citas verificables",
          description:
            "Abre las citas para revisar el documento fuente exacto.",
        },
      },
      {
        element: '[data-tour-id="ai-feedback"]',
        popover: {
          title: "Feedback",
          description:
            "Marca respuestas útiles o agrega comentarios para mejorar la calidad.",
        },
      },
    ],
  },
  {
    id: "document-editor-ai",
    title: "Edición asistida por IA",
    subtitle: "Drafting, colaboración, cola IA y comentarios",
    category: "Documentos",
    core: true,
    icon: "edit",
    steps: 10,
    durationMin: 3,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="collab-presence-avatars"]',
        popover: {
          title: "Editores conectados",
          description:
            "Aquí ves quién está editando el documento en tiempo real.",
        },
      },
      {
        element: '[data-tour-id="collab-sync-status"]',
        popover: {
          title: "Estado de sincronización",
          description:
            "El indicador confirma si el estado Yjs está sincronizado o reconectando.",
        },
      },
      {
        element: '[data-tour-id="doc-autosave-toggle"]',
        popover: {
          title: "Autoguardado",
          description:
            "Controla si tus ediciones se guardan como borrador mientras trabajas.",
        },
      },
      {
        element: '[data-tour-id="doc-save-version"]',
        popover: {
          title: "Nueva versión",
          description:
            "Crea una versión persistida con notas para el historial.",
        },
      },
      {
        element: '[data-tour-id="doc-export-menu"]',
        popover: {
          title: "Exportar limpio",
          description: "Descarga DOCX/PDF con o sin comentarios.",
        },
      },
      {
        element: '[data-tour-id="doc-editor-options-menu"]',
        popover: {
          title: "Edición",
          description:
            "Abre este menú para cambiar entre editar, sugerir o comentar, y ajustar el formato.",
        },
      },
      {
        element: '[data-tour-id="comment-anchored-highlight"]',
        popover: {
          title: "Comentarios anclados",
          description:
            "Los hilos saltan al rango exacto y se mantienen junto al texto.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-ai"]',
        popover: {
          title: "Pedidos IA",
          description:
            "La cola permite lanzar varios pedidos y seguir editando.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-risks"]',
        popover: {
          title: "Riesgos",
          description: "Revisa hallazgos y salta al texto relacionado.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-comments"]',
        popover: {
          title: "Comentarios",
          description: "Filtra, responde y resuelve hilos.",
        },
      },
    ],
  },
  {
    id: "templates-basics",
    title: "Plantillas de documentos",
    subtitle: "Variables, cláusulas, IA y generación",
    category: "Plantillas",
    core: true,
    icon: "template",
    steps: 6,
    durationMin: 3,
    matchPath: /^\/templates(\/.*)?$/,
    navigateTo: "/templates",
    build: () => [
      {
        element: '[data-tour-id="templates-new-button"]',
        popover: {
          title: "Nueva plantilla",
          description:
            "Crea un modelo reutilizable (contrato, NDA, poder, política) y edítalo con variables, cláusulas y secciones condicionales.",
        },
      },
      {
        element: '[data-tour-id="templates-ai-compose-button"]',
        popover: {
          title: "Crear con IA",
          description:
            "Describe lo que necesitas y la IA propone un borrador de plantilla por entrevista; tú revisas y ajustas antes de guardar.",
        },
      },
      {
        element: '[data-tour-id="templates-import-button"]',
        popover: {
          title: "Importar documento",
          description:
            "Sube un Word, Markdown o texto y conviértelo en plantilla editable conservando el formato.",
        },
      },
      {
        element: '[data-tour-id="templates-table"]',
        popover: {
          title: "Biblioteca de plantillas",
          description:
            "Filtra entre plantillas del tenant y globales. Abre una para editar su contenido, declarar variables e insertar cláusulas desde la biblioteca.",
        },
      },
      fallbackStep(
        "Variables y cláusulas",
        "Dentro del editor, la barra de herramientas inserta variables ({{campo}}), bloques condicionales y referencias de cláusula del repositorio del estudio.",
      ),
      fallbackStep(
        "Generar el documento",
        "Desde una plantilla publicada usa Generar: completa las variables (o Autollenar desde un expediente) y descarga el documento final en DOCX o PDF.",
      ),
    ],
  },
  {
    id: "playbook-compare",
    title: "Comparar contra Playbook",
    subtitle: "4 niveles y gap analysis",
    category: "Documentos",
    icon: "scale",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/documents\/.+\/playbook/,
    build: () => [
      fallbackStep(
        "Elige Playbook",
        "Selecciona NDA Peruano, MSA SaaS o un playbook propio del estudio para comparar el documento.",
      ),
      {
        element: '[data-tour-id="playbook-score"]',
        popover: {
          title: "Score global",
          description: "Mide alineamiento frente al estándar esperado.",
        },
      },
      {
        element: '[data-tour-id="playbook-breakdown"]',
        popover: {
          title: "Breakdown",
          description: "Ideal, aceptable, mínimo, no negociable y faltante.",
        },
      },
      fallbackStep(
        "Drill-down por cláusula",
        "Cada fila del resultado permite saltar al párrafo correspondiente del documento.",
      ),
      fallbackStep(
        "Exportar evidencia",
        "Genera un PDF/XLSX con branding del tenant para anexar a la revisión legal.",
      ),
    ],
  },
  {
    id: "risks-analysis",
    title: "Análisis de riesgos legales",
    subtitle: "Categorías, scoring, AST y heatmap",
    category: "Riesgos",
    icon: "alert",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/risks/,
    build: () => [
      fallbackStep(
        "Riesgos legales",
        "Usa filtros por categoría y severidad; el AST permite mapear riesgos por sección del documento.",
      ),
    ],
  },
  {
    id: "sidebar-collapse",
    title: "Colapsar el menú a íconos",
    subtitle: "Sidebar estilo Linear/Notion",
    category: "Navegación",
    icon: "panelLeftClose",
    steps: 3,
    durationMin: 1,
    matchPath: /^\//,
    build: () => [
      {
        element: '[data-tour-id="sidebar-collapse-toggle"]',
        popover: {
          title: "Colapsar menú",
          description:
            "Alterna entre navegación completa y modo icon-only. También funciona con Cmd/Ctrl+B.",
        },
      },
      {
        element: '[data-tour-id="sidebar-collapse-root"]',
        popover: {
          title: "Estado persistente",
          description:
            "El modo queda guardado por navegador para recuperar tu densidad preferida.",
        },
      },
      fallbackStep(
        "Tooltips",
        "En modo icon-only, cada ícono conserva nombre accesible y tooltip lateral.",
      ),
    ],
  },
  {
    id: "zoom-pagination",
    title: "Zoom y paginación del documento",
    subtitle: "Controles tipo Docs/PDF",
    category: "Documentos",
    icon: "zoomIn",
    steps: 4,
    durationMin: 1,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="zoom-pagination"]',
        popover: {
          title: "Zoom del documento",
          description:
            "Usa presets, ajustar ancho o atajos Cmd/Ctrl +, Cmd/Ctrl - y Cmd/Ctrl 0.",
        },
      },
      fallbackStep(
        "Paginación",
        "PageUp y PageDown navegan el indicador de página.",
      ),
      fallbackStep(
        "Conteo",
        "El footer muestra palabras, caracteres y estado de sincronización.",
      ),
      {
        element: '[data-tour-id="ast-structure-panel"]',
        popover: {
          title: "AST",
          description:
            "El acceso al AST sigue disponible desde el footer y el panel derecho.",
        },
      },
    ],
  },
  {
    id: "ai-refresh-scope",
    title: "Refrescar IA y cambiar scope",
    subtitle: "Cola IA más legible",
    category: "Documentos",
    icon: "refreshCw",
    steps: 4,
    durationMin: 1,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="ai-refresh-scope"]',
        popover: {
          title: "Refrescar IA",
          description:
            "Fuerza la actualización de la cola sin esperar el polling.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-ai"]',
        popover: {
          title: "Cola persistente",
          description:
            "Cada pedido muestra estado, ID corto con tooltip y resultado aceptable.",
        },
      },
      {
        element: '[data-tour-id="doc-tone-picker"]',
        popover: {
          title: "Tono",
          description:
            "El tono queda alineado con el botón de envío en la barra sticky.",
        },
      },
      {
        element: '[data-tour-id="ai-input"]',
        popover: {
          title: "Input sticky",
          description:
            "El prompt se mantiene visible al revisar pedidos largos.",
        },
      },
    ],
  },
  {
    id: "save-controls",
    title: "Guardar versión y autoguardado",
    subtitle: "Controles persistentes en header",
    category: "Documentos",
    icon: "save",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="save-controls"]',
        popover: {
          title: "Guardar siempre visible",
          description:
            "El botón de versión y el estado de guardado ya no viven enterrados en un menú.",
        },
      },
      {
        element: '[data-tour-id="doc-autosave-toggle"]',
        popover: {
          title: "Autoguardado",
          description:
            "El switch se persiste por usuario y emite evento auditable.",
        },
      },
      {
        element: '[data-tour-id="doc-save-version"]',
        popover: {
          title: "Split button",
          description:
            "Abre opciones para crear versión, guardar borrador o descartar cambios.",
        },
      },
      fallbackStep(
        "Atajos de guardado",
        "Cmd/Ctrl+S abre notas, Cmd/Ctrl+Shift+S guarda rápido.",
      ),
      fallbackStep(
        "Cambios sin guardar",
        "Con autoguardado apagado, el badge de warning queda prominente.",
      ),
    ],
  },
  {
    id: "mobile-editor",
    title: "Editor en móvil",
    subtitle: "Drawer y bottom sheet",
    category: "Documentos",
    icon: "smartphone",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="mobile-editor-ai-button"]',
        popover: {
          title: "Panel inferior",
          description:
            "En móvil, IA, riesgos y comentarios se abren desde el bottom sheet.",
        },
      },
      fallbackStep(
        "Menú lateral",
        "El botón de menú abre el drawer de navegación del AppShell.",
      ),
      fallbackStep(
        "Toolbar inferior",
        "Los modos Editar, Sugerir y Comentar quedan como iconos táctiles.",
      ),
      fallbackStep(
        "Guardar móvil",
        "El acceso rápido de versión vive en la barra inferior.",
      ),
      fallbackStep(
        "Tabs móviles",
        "Las secciones usan overflow cuando falta espacio horizontal.",
      ),
    ],
  },
  {
    id: "keyboard-shortcuts",
    title: "Atajos de teclado",
    subtitle: "Catálogo completo",
    category: "Navegación",
    icon: "keyboard",
    steps: 6,
    durationMin: 1,
    matchPath: /^\//,
    build: () => [
      {
        element: '[data-tour-id="keyboard-shortcuts"]',
        popover: {
          title: "Catálogo",
          description: "Cmd/Ctrl+/ abre esta referencia en cualquier pantalla.",
        },
      },
      fallbackStep("Sidebar", "Cmd/Ctrl+B alterna el sidebar."),
      fallbackStep(
        "Guardado",
        "Cmd/Ctrl+S, Shift+S y Shift+Alt+S cubren versión y autosave.",
      ),
      fallbackStep(
        "Zoom",
        "Cmd/Ctrl +, Cmd/Ctrl - y Cmd/Ctrl 0 controlan zoom.",
      ),
      fallbackStep(
        "Modos",
        "Cmd/Ctrl+1, 2 y 3 cambian Editar/Sugerir/Comentar.",
      ),
      fallbackStep(
        "IA",
        "Cmd/Ctrl+J enfoca el input IA y Cmd/Ctrl+R refresca la cola.",
      ),
    ],
  },
  {
    id: "collab-realtime",
    title: "Edición colaborativa en tiempo real",
    subtitle: "Yjs, presencia y reconexión",
    category: "Documentos",
    icon: "users",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/documents\/[^/]+$/,
    build: () => [
      {
        element: '[data-tour-id="collab-presence-avatars"]',
        popover: {
          title: "Presencia",
          description:
            "Los avatares muestran editores activos en este documento.",
        },
      },
      {
        element: '[data-tour-id="collab-sync-status"]',
        popover: {
          title: "Sincronización",
          description: "El estado cambia cuando la conexión cae o se recupera.",
        },
      },
      {
        element: '[data-tour-id="ast-structure-panel"]',
        popover: {
          title: "Estructura compartida",
          description:
            "El esquema se actualiza sobre el contenido sincronizado.",
        },
      },
      {
        element: '[data-tour-id="doc-autosave-toggle"]',
        popover: {
          title: "Persistencia",
          description:
            "Yjs sincroniza la sesión; el autoguardado conserva borradores.",
        },
      },
      {
        element: '[data-tour-id="doc-save-version"]',
        popover: {
          title: "Cerrar una versión",
          description: "Cuando el equipo termina, crea una versión con notas.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-comments"]',
        popover: {
          title: "Discusión lateral",
          description: "Los comentarios complementan la edición colaborativa.",
        },
      },
    ],
  },
  {
    id: "ocr-pdf-scanned",
    title: "OCR para PDFs escaneados",
    subtitle: "Tesseract, indexación y RAG",
    category: "Documentos",
    icon: "doc",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/documents/,
    build: () => [
      {
        element: '[data-tour-id="ocr-badge"]',
        popover: {
          title: "OCR aplicado",
          description:
            "El badge indica que un PDF sin capa de texto fue procesado.",
        },
      },
      {
        element: '[data-tour-id="ocr-status-spinner"]',
        popover: {
          title: "Procesamiento",
          description:
            "Mientras corre OCR, el documento sigue visible con estado pendiente.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-ai"]',
        popover: {
          title: "Texto indexado",
          description:
            "Al terminar OCR, las skills IA pueden leer el contenido.",
        },
      },
      {
        element: '[data-tour-id="ast-structure-panel"]',
        popover: {
          title: "Estructura",
          description: "El AST jurídico se genera usando el texto recuperado.",
        },
      },
    ],
  },
  {
    id: "semantic-diff",
    title: "Diff visual semántico",
    subtitle: "Cláusulas, montos y definiciones",
    category: "Documentos",
    icon: "diff",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/documents\/.+\/diff/,
    build: () => [
      {
        element: '[data-tour-id="semantic-diff-summary"]',
        popover: {
          title: "Resumen semántico",
          description:
            "El sistema resume cambios legales relevantes entre versiones.",
        },
      },
      {
        element: '[data-tour-id="semantic-diff-clause-change"]',
        popover: {
          title: "Cambio de cláusula",
          description: "Las modificaciones se agrupan por tipo de cláusula.",
        },
      },
      {
        element: '[data-tour-id="semantic-diff-amount-change"]',
        popover: {
          title: "Cambio económico",
          description:
            "Montos, monedas y fechas clave aparecen como cambios estructurados.",
        },
      },
      {
        element: '[data-tour-id="ast-clause-highlight"]',
        popover: {
          title: "AST como base",
          description:
            "El diff semántico aprovecha cláusulas y rangos del AST jurídico.",
        },
      },
      {
        element: '[data-tour-id="doc-export-menu"]',
        popover: {
          title: "Evidencia",
          description: "Exporta el resultado para anexarlo a revisión legal.",
        },
      },
    ],
  },
  {
    id: "ai-admin-models",
    title: "Administrar modelos IA",
    subtitle: "Catálogo, profiles y overrides",
    category: "Administración",
    icon: "settings",
    steps: 6,
    durationMin: 3,
    matchPath: /^\/admin\/system-config\/ai/,
    navigateTo: "/admin/system-config/ai",
    build: () => [
      {
        element: '[data-tour-id="ai-config-models"]',
        popover: {
          title: "Catálogo",
          description: "Activa o desactiva modelos Vertex aprobados.",
        },
      },
      {
        element: '[data-tour-id="ai-config-profiles"]',
        popover: {
          title: "Profiles",
          description:
            "Define qué modelo usa quality, fast, reviewer y long-context.",
        },
      },
      {
        element: '[data-tour-id="ai-config-skills"]',
        popover: {
          title: "Skills",
          description: "Sobrescribe profile o modelo para una skill concreta.",
        },
      },
      fallbackStep(
        "Auditoria de configuracion",
        "Cada cambio de modelos, profiles y bindings emite eventos auditables con hash-chain.",
      ),
    ],
  },
  {
    id: "cost-monitoring",
    title: "Monitorear costo IA",
    subtitle: "Routing y ahorro",
    category: "Costos IA",
    icon: "gauge",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/admin\/cost/,
    build: () => [
      {
        element: '[data-tour-id="cost-dashboard-filters"]',
        popover: {
          title: "Filtros financieros",
          description:
            "Ajusta período, moneda y dimensión para analizar gasto IA sin salir del panel.",
        },
      },
      {
        element: '[data-tour-id="cost-total-kpi"]',
        popover: {
          title: "Gasto total",
          description:
            "Resume cost events ya atribuidos a tenant, proyecto, contraparte, usuario, skill y modelo.",
        },
      },
      {
        element: '[data-tour-id="cost-forecast-kpi"]',
        popover: {
          title: "Forecast EOM",
          description:
            "Proyecta el gasto de fin de mes con banda de variación para anticipar excesos de budget.",
        },
      },
      {
        element: '[data-tour-id="cost-drilldown-table"]',
        popover: {
          title: "Drilldown",
          description:
            "Agrupa por skill, modelo, usuario, proyecto, contraparte o provider para encontrar drivers.",
        },
      },
      {
        element: '[data-tour-id="cost-recommendations-list"]',
        popover: {
          title: "Optimización",
          description:
            "Revisa sugerencias de ahorro cuando hay gasto premium que podría enrutar a modelos rápidos.",
        },
      },
      {
        element: '[data-tour-id="chargeback-invoice-panel"]',
        popover: {
          title: "Chargeback",
          description:
            "Convierte costos billables por contraparte en invoices con markup y trazabilidad.",
        },
      },
    ],
  },
  {
    id: "cost-dashboard-drilldown",
    title: "Dashboard de costos con drilldown",
    subtitle: "6 dimensiones y moneda USD/PEN",
    category: "Costos IA",
    icon: "gauge",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/admin\/cost$/,
    navigateTo: "/admin/cost",
    build: () => [
      {
        element: '[data-tour-id="cost-dashboard-filters"]',
        popover: {
          title: "Rango y moneda",
          description:
            "Define el período y cambia entre USD y PEN para ver la lectura financiera correcta.",
        },
      },
      {
        element: '[data-tour-id="cost-drilldown-dimension"]',
        popover: {
          title: "Dimensión",
          description:
            "Elige si quieres agrupar por skill, modelo, usuario, proyecto, contraparte o provider.",
        },
      },
      {
        element: '[data-tour-id="cost-total-kpi"]',
        popover: {
          title: "Total atribuido",
          description: "El total ya usa la atribución S31 sobre cost_event.",
        },
      },
      {
        element: '[data-tour-id="cost-efficiency-kpi"]',
        popover: {
          title: "Eficiencia",
          description:
            "Costo promedio por request para detectar deterioros de routing.",
        },
      },
      {
        element: '[data-tour-id="cost-drilldown-table"]',
        popover: {
          title: "Tabla accionable",
          description:
            "Ordena mentalmente los mayores drivers antes de bajar a una sub-vista operativa.",
        },
      },
      {
        element: '[data-tour-id="cost-export-btn"]',
        popover: {
          title: "Exportar",
          description:
            "Genera evidencia para revisión financiera o conversación con el cliente.",
        },
      },
    ],
  },
  {
    id: "cost-budget-multilevel",
    title: "Configurar budget multi-nivel",
    subtitle: "Tenant, proyecto y usuario",
    category: "Costos IA",
    icon: "lock",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/admin\/cost\/budgets/,
    navigateTo: "/admin/cost",
    build: () => [
      {
        element: '[data-tour-id="cost-budget-table"]',
        popover: {
          title: "Budgets activos",
          description:
            "Cada fila muestra budget, consumo y umbrales warn/throttle/hard-stop.",
        },
      },
      {
        element: '[data-tour-id="cost-budget-create-btn"]',
        popover: {
          title: "Crear budget",
          description: "Crea un budget tenant demo para activar enforcement.",
        },
      },
      {
        element: '[data-tour-id="cost-currency-badge"]',
        popover: {
          title: "Tipo de cambio",
          description:
            "La conversión PEN/USD queda visible para decisiones locales.",
        },
      },
      {
        element: '[data-tour-id="cost-forecast-kpi"]',
        popover: {
          title: "Predicción",
          description:
            "Usa forecast para saber si los thresholds se alcanzarán antes del cierre del mes.",
        },
      },
      {
        element: '[data-tour-id="cost-recommendations-list"]',
        popover: {
          title: "Ahorro preventivo",
          description:
            "Apoya throttle con recomendaciones de routing antes del hard-stop.",
        },
      },
    ],
  },
  {
    id: "cost-anomaly-investigate",
    title: "Investigar anomalía de costo",
    subtitle: "3 sigma y resolución",
    category: "Costos IA",
    icon: "alert",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/admin\/cost\/anomalies/,
    navigateTo: "/admin/cost",
    build: () => [
      {
        element: '[data-tour-id="cost-anomaly-list"]',
        popover: {
          title: "Lista de anomalías",
          description: "Cada anomalía compara el gasto real contra media + 3σ.",
        },
      },
      {
        element: '[data-tour-id="cost-anomaly-evaluate-btn"]',
        popover: {
          title: "Evaluar ahora",
          description:
            "Dispara manualmente el detector horario para validar un pico.",
        },
      },
      {
        element: '[data-tour-id="cost-anomaly-resolve-btn"]',
        popover: {
          title: "Cerrar investigación",
          description:
            "Marca el hallazgo como resuelto cuando el driver está explicado.",
        },
      },
      {
        element: '[data-tour-id="cost-drilldown-table"]',
        popover: {
          title: "Drivers",
          description:
            "Contrasta el hallazgo con el drilldown por skill/modelo.",
        },
      },
    ],
  },
  {
    id: "cost-recommendations",
    title: "Aplicar recomendaciones de optimización",
    subtitle: "Routing y ahorro potencial",
    category: "Costos IA",
    icon: "wand",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/admin\/cost/,
    navigateTo: "/admin/cost",
    build: () => [
      {
        element: '[data-tour-id="cost-recommendations-savings"]',
        popover: {
          title: "Ahorro potencial",
          description:
            "Resume lo que se podría ahorrar moviendo gasto premium a profiles rápidos.",
        },
      },
      {
        element: '[data-tour-id="cost-recommendations-list"]',
        popover: {
          title: "Lista de acciones",
          description:
            "Cada recomendación muestra skill, modelo actual, recomendado, impacto y racional.",
        },
      },
      {
        element: '[data-tour-id="cost-drilldown-table"]',
        popover: {
          title: "Validación",
          description:
            "Usa el drilldown para confirmar que el gasto justifica el cambio.",
        },
      },
      {
        element: '[data-tour-id="cost-budget-table"]',
        popover: {
          title: "Enforcement",
          description:
            "Combina recomendaciones con budgets para evitar bloqueos duros.",
        },
      },
    ],
  },
  {
    id: "chargeback-invoice",
    title: "Generar invoice cliente con markup",
    subtitle: "Costo IA trazable a contraparte",
    category: "Costos IA",
    icon: "building",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/admin\/cost\/chargeback/,
    navigateTo: "/admin/cost",
    build: () => [
      {
        element: '[data-tour-id="chargeback-invoice-panel"]',
        popover: {
          title: "Chargeback",
          description:
            "Lista costos billables agrupados por contraparte para facturación cliente.",
        },
      },
      {
        element: '[data-tour-id="chargeback-generate-invoice-btn"]',
        popover: {
          title: "Generar invoice",
          description:
            "Crea el borrador de invoice con markup y totales USD/PEN.",
        },
      },
      {
        element: '[data-tour-id="cost-currency-badge"]',
        popover: {
          title: "Moneda local",
          description:
            "El tipo de cambio se guarda para trazabilidad fiscal peruana.",
        },
      },
      {
        element: '[data-tour-id="cost-export-btn"]',
        popover: {
          title: "Exportar evidencia",
          description: "Comparte el soporte financiero de la factura.",
        },
      },
      {
        element: '[data-tour-id="cost-total-kpi"]',
        popover: {
          title: "Conciliación",
          description: "Cruza el total del período con el detalle por cliente.",
        },
      },
    ],
  },
  {
    id: "search-rag",
    title: "Búsqueda semántica RAG",
    subtitle: "Híbrida y multi-tenant",
    category: "Búsqueda RAG",
    icon: "search",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/rag/,
    build: () => [
      fallbackStep(
        "Búsqueda RAG",
        "Combina búsqueda semántica y filtros de seguridad por tenant.",
      ),
    ],
  },
  {
    id: "rag-scope-picker",
    title: "Filtrar IA por scope",
    subtitle: "Documento, proyecto, contraparte y tenant",
    category: "Búsqueda RAG",
    icon: "database",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/(documents\/.+|ai|rag)/,
    build: () => {
      const path = window.location.pathname;
      const base: DriveStep[] = [
        {
          element: '[data-tour-id="ai-scope-picker"]',
          popover: {
            title: "Elige el scope",
            description:
              "El scope define de donde toma contexto la IA: documento, proyecto, contraparte, tenant o fuentes globales.",
          },
        },
        {
          element: '[data-tour-id="ai-scope-badge"]',
          popover: {
            title: "Scope activo",
            description:
              "Cada respuesta deja visible el contexto usado para que puedas auditar la busqueda.",
          },
        },
      ];
      if (path.startsWith("/rag")) {
        return [
          ...base,
          {
            element: '[data-tour-id="rag-search-scope-filter"]',
            popover: {
              title: "Filtro de busqueda",
              description:
                "En RAG exploratorio puedes cambiar el scope antes de lanzar la busqueda.",
            },
          },
          {
            element: '[data-tour-id="rag-search-result-source"]',
            popover: {
              title: "Fuente del resultado",
              description:
                "Cada resultado muestra de que scope y fuente proviene.",
            },
          },
          {
            element: '[data-tour-id="rag-boost-debug-panel"]',
            popover: {
              title: "Score con boost",
              description:
                "El score ya incluye el boost por documento, proyecto, contraparte o matter type.",
            },
          },
        ];
      }
      if (path.startsWith("/documents")) {
        return [
          ...base,
          {
            element: '[data-tour-id="doc-rail-ai"]',
            popover: {
              title: "IA contextual",
              description:
                "Los pedidos del editor usan el scope elegido y lo guardan con trazabilidad.",
            },
          },
          {
            element: '[data-tour-id="doc-rail-risks"]',
            popover: {
              title: "Riesgos con contexto",
              description:
                "El analisis puede priorizar el documento o ampliar a proyecto/contraparte.",
            },
          },
          {
            element: '[data-tour-id="doc-rail-comments"]',
            popover: {
              title: "Revision asistida",
              description:
                "Combina comentarios humanos con respuestas IA acotadas al contexto correcto.",
            },
          },
        ];
      }
      return [
        ...base,
        {
          element: '[data-tour-id="ai-input"]',
          popover: {
            title: "Pregunta con contexto",
            description:
              "La pregunta se envia junto con el scope para priorizar chunks relevantes.",
          },
        },
        {
          element: '[data-tour-id="ai-citation"]',
          popover: {
            title: "Citas por scope",
            description:
              "Las citas ayudan a validar si la respuesta vino del corpus esperado.",
          },
        },
        {
          element: '[data-tour-id="ai-feedback"]',
          popover: {
            title: "Feedback",
            description:
              "Marca respuestas malas si el scope elegido no fue suficiente.",
          },
        },
      ];
    },
  },
  {
    id: "counterparty-insights",
    title: "Insights cross-document de contraparte",
    subtitle: "Patrones, riesgos y tendencias",
    category: "Contrapartes",
    core: true,
    icon: "users",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/counterparties\/.+/,
    build: () => [
      {
        element: '[data-tour-id="counterparty-insights-card"]',
        popover: {
          title: "Insights IA",
          description:
            "La IA consolida contratos y proyectos asociados a la contraparte.",
        },
      },
      {
        element: '[data-tour-id="counterparty-most-negotiated-clauses"]',
        popover: {
          title: "Clausulas negociadas",
          description:
            "Detecta que clausulas suelen moverse mas con esta contraparte.",
        },
      },
      {
        element: '[data-tour-id="counterparty-trends"]',
        popover: {
          title: "Tendencias",
          description:
            "Compara cambios historicos como plazos, montos o posiciones contractuales.",
        },
      },
      {
        element: '[data-tour-id="counterparty-recommendations"]',
        popover: {
          title: "Recomendaciones",
          description:
            "Las acciones sugeridas se basan en patrones de todos los documentos de la contraparte.",
        },
      },
    ],
  },
  {
    id: "project-ai-chat",
    title: "Chat IA contextual del proyecto",
    subtitle: "Todos los documentos del caso",
    category: "Expedientes",
    core: true,
    icon: "folder",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/matters\/.+\/ai/,
    build: () => [
      {
        element: '[data-tour-id="project-ai-context-badge"]',
        popover: {
          title: "Contexto del proyecto",
          description:
            "El chat ya conoce documentos, contraparte y matter type del proyecto.",
        },
      },
      {
        element: '[data-tour-id="ai-thread-sidebar"]',
        popover: {
          title: "Threads del proyecto",
          description:
            "Las conversaciones quedan filtradas por `projectId` y se pueden retomar.",
        },
      },
      {
        element: '[data-tour-id="rag-jurisprudencia-toggle"]',
        popover: {
          title: "Jurisprudencia opcional",
          description:
            "Activa fuentes globales cuando necesites precedentes publicos ademas del corpus privado.",
        },
      },
      {
        element: '[data-tour-id="project-ai-input"]',
        popover: {
          title: "Pregunta al proyecto",
          description:
            "Pide resumen de riesgos, obligaciones o estrategia de negociacion para todo el caso.",
        },
      },
      {
        element: '[data-tour-id="ai-scope-badge"]',
        popover: {
          title: "Scope aplicado",
          description:
            "El badge confirma si la respuesta uso proyecto o proyecto + jurisprudencia.",
        },
      },
    ],
  },
  {
    id: "audit-timeline",
    title: "Ver timeline de auditoria",
    subtitle: "Eventos, payload y hash-chain",
    category: "Auditoría",
    icon: "history",
    steps: 5,
    durationMin: 2,
    matchPath:
      /^\/(documents|projects|counterparties|workflows|admin\/tenants)\/.+/,
    build: () => [
      {
        element: '[data-tour-id="audit-activity-tab"]',
        popover: {
          title: "Actividad auditable",
          description:
            "Este panel lista eventos de negocio append-only para la entidad abierta.",
        },
      },
      {
        element: '[data-tour-id="audit-timeline-filter"]',
        popover: {
          title: "Filtro por evento",
          description:
            "Filtra por `event_type` para aislar cambios de documentos, workflows, riesgos o configuracion.",
        },
      },
      {
        element: '[data-tour-id="audit-hash-badge"]',
        popover: {
          title: "Integridad",
          description:
            "El badge muestra si el evento verifico correctamente contra la hash-chain.",
        },
      },
      {
        element: '[data-tour-id="audit-event-payload-modal"]',
        popover: {
          title: "Payload completo",
          description:
            "Haz click en un evento para ver el JSON completo, hash, secuencia y marcas de PII/TSA.",
        },
      },
      fallbackStep(
        "Export legal",
        "Desde auditoria puedes generar un paquete legal PDF con la cadena de hashes.",
      ),
    ],
  },
  {
    id: "compliance-requests",
    title: "Gestionar pedidos GDPR/LPDP",
    subtitle: "Access, erasure y SLA legal",
    category: "Auditoría",
    icon: "shield",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/admin\/compliance\/requests/,
    build: () => [
      fallbackStep(
        "Pedidos compliance",
        "Registra solicitudes ACCESS, ERASURE, RECTIFICATION, PORTABILITY y OBJECTION con SLA de 30 dias.",
      ),
      fallbackStep(
        "Nuevo pedido",
        "El boton Nuevo crea el caso y calcula `due_date` legal automaticamente.",
      ),
      fallbackStep(
        "SLA",
        "Los pedidos pendientes o en progreso se ordenan por vencimiento.",
      ),
      fallbackStep(
        "Access export",
        "ACCESS genera un manifest trazable sin alterar la cadena de auditoria.",
      ),
      fallbackStep(
        "Erasure",
        "El borrado preserva audit_event y enmascara PII en tablas de negocio integradas.",
      ),
      fallbackStep(
        "Cierre",
        "Completar o denegar exige notas para dejar base legal visible.",
      ),
    ],
  },
  {
    id: "audit-legal-export",
    title: "Exportar paquete legal con sello",
    subtitle: "PDF, hashes y TSA",
    category: "Auditoría",
    icon: "download",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/audit\/legal-export/,
    build: () => [
      fallbackStep(
        "Paquete legal",
        "El export PDF incluye eventos cronologicos, hash de cada evento y estado de verificacion.",
      ),
      fallbackStep(
        "Rango temporal",
        "Define `from` y `to` para acotar evidencia por periodo.",
      ),
      fallbackStep(
        "Sello TSA",
        "Cuando KMS esta activo, el evento muestra signer y algoritmo.",
      ),
      fallbackStep(
        "Cadena inmutable",
        "El PDF nunca modifica audit_event; solo lee y certifica.",
      ),
    ],
  },
  {
    id: "inbox-essentials",
    title: "Tu bandeja de entrada",
    subtitle: "Filtros, hilos y acciones masivas",
    category: "Notificaciones",
    icon: "inbox",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/notifications/,
    navigateTo: "/notifications",
    build: () => [
      {
        element: '[data-tour-id="inbox-folder-sidebar"]',
        popover: {
          title: "Carpetas",
          description:
            "Filtra por no leídas, menciones, acciones requeridas o archivadas.",
        },
      },
      {
        element: '[data-tour-id="inbox-search-bar"]',
        popover: {
          title: "Búsqueda",
          description:
            "Busca por asunto, cuerpo de la notificación o referencia.",
        },
      },
      {
        element: '[data-tour-id="inbox-thread-group"]',
        popover: {
          title: "Hilos",
          description:
            "Las notificaciones relacionadas se agrupan por thread para reducir ruido.",
        },
      },
      {
        element: '[data-tour-id="inbox-bulk-select"]',
        popover: {
          title: "Acciones masivas",
          description:
            "Selecciona varias notificaciones y márcalas leídas, archívalas o descártalas.",
        },
      },
      {
        element: '[data-tour-id="notif-status-badge"]',
        popover: {
          title: "Estado",
          description:
            "NEW, DELIVERED, READ, ARCHIVED, ACTIONED y DISMISSED reflejan el ciclo de vida real.",
        },
      },
      {
        element: '[data-tour-id="notif-priority-badge"]',
        popover: {
          title: "Prioridad",
          description:
            "HIGH y URGENT ayudan a priorizar mentions y acciones vencidas.",
        },
      },
    ],
  },
  {
    id: "notification-preferences",
    title: "Configurar preferencias",
    subtitle: "Tipos y canales",
    category: "Notificaciones",
    icon: "settings",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/settings\/notifications/,
    navigateTo: "/settings/notifications",
    build: () => [
      {
        element: '[data-tour-id="preference-matrix"]',
        popover: {
          title: "Matriz por canal",
          description:
            "Activa o apaga in-app, email, push y SMS por tipo de notificación.",
        },
      },
      {
        element: '[data-tour-id="preference-preset-buttons"]',
        popover: {
          title: "Presets rápidos",
          description: "Cambia a solo críticos, todo activado o solo in-app.",
        },
      },
      {
        element: '[data-tour-id="digest-toggle"]',
        popover: {
          title: "Digest",
          description: "Agrupa novedades para evitar spam operativo.",
        },
      },
      {
        element: '[data-tour-id="push-permission-banner"]',
        popover: {
          title: "Push",
          description:
            "El navegador puede mostrar notificaciones aunque la pestaña no esté activa.",
        },
      },
      {
        element: '[data-tour-id="ws-realtime-indicator"]',
        popover: {
          title: "Realtime",
          description:
            "El indicador muestra si la sesión recibe eventos por WebSocket.",
        },
      },
    ],
  },
  {
    id: "mentions-collab",
    title: "Mencionar a alguien con @",
    subtitle: "Comentarios y colaboración",
    category: "Documentos",
    icon: "at",
    steps: 4,
    durationMin: 1,
    matchPath: /^\/documents\/.+/,
    build: () => [
      {
        element: '[data-tour-id="doc-editor-options-menu"]',
        popover: {
          title: "Modo comentario",
          description:
            "Selecciona texto, abre Edición y elige Comentar para anclar un hilo al rango exacto del documento.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-comments"]',
        popover: {
          title: "Autocompletar con @",
          description:
            "Dentro de un comentario, escribe @ y elige a un usuario del tenant por nombre o correo. La mención se guarda resoluble y dispara una notificación HIGH para esa persona.",
        },
      },
      {
        element: '[data-tour-id="comment-anchored-highlight"]',
        popover: {
          title: "Comentario anclado",
          description:
            "El resaltado conecta el hilo con su rango de texto; al hacer clic, el editor salta al párrafo mencionado.",
        },
      },
      {
        element: '[data-tour-id="doc-rail-comments"]',
        popover: {
          title: "Hilo de discusión",
          description:
            "Las respuestas también aceptan @menciones y quedan ordenadas en el hilo hasta que lo resuelvas.",
        },
      },
    ],
  },
  {
    id: "digest-setup",
    title: "Configurar digest",
    subtitle: "Resumen diario o semanal",
    category: "Notificaciones",
    icon: "mail",
    steps: 4,
    durationMin: 1,
    matchPath: /^\/settings\/notifications/,
    navigateTo: "/settings/notifications",
    build: () => [
      {
        element: '[data-tour-id="digest-toggle"]',
        popover: {
          title: "Activar digest",
          description: "El toggle controla si se envían resúmenes.",
        },
      },
      {
        element: '[data-tour-id="digest-frequency-picker"]',
        popover: {
          title: "Frecuencia",
          description: "Elige diario, semanal o deshabilitado.",
        },
      },
      {
        element: '[data-tour-id="digest-preview"]',
        popover: {
          title: "Preview",
          description:
            "El digest agrupa menciones, tareas, documentos y riesgos por hilo.",
        },
      },
      {
        element: '[data-tour-id="preference-matrix"]',
        popover: {
          title: "Compatibilidad",
          description:
            "Las preferencias por canal se respetan antes de enviar.",
        },
      },
    ],
  },
  {
    id: "push-enable",
    title: "Activar push del browser",
    subtitle: "Notificaciones nativas",
    category: "Notificaciones",
    icon: "bell",
    steps: 3,
    durationMin: 1,
    matchPath: /^\/settings\/notifications/,
    navigateTo: "/settings/notifications",
    build: () => [
      {
        element: '[data-tour-id="push-permission-banner"]',
        popover: {
          title: "Permiso push",
          description: "El navegador pedirá permiso antes de suscribirse.",
        },
      },
      {
        element: '[data-tour-id="preference-matrix"]',
        popover: {
          title: "Canal push",
          description:
            "Activa push solo para los tipos que realmente deben interrumpir.",
        },
      },
      {
        element: '[data-tour-id="ws-realtime-indicator"]',
        popover: {
          title: "Fallback realtime",
          description:
            "Si estás conectado, el WebSocket actualiza la bandeja sin polling.",
        },
      },
    ],
  },
  {
    id: "dashboards-canonical",
    title: "5 dashboards canonicos",
    subtitle: "CEO, CFO, Legal, Matter y Compliance",
    category: "Dashboards",
    icon: "bars",
    steps: 6,
    durationMin: 2,
    matchPath: /^\/dashboards/,
    navigateTo: "/dashboards",
    build: () => [
      {
        element: '[data-tour-id="dashboards-hub"]',
        popover: {
          title: "Hub de dashboards",
          description:
            "Aqui se concentran los dashboards canonicos por stakeholder.",
        },
      },
      {
        element: '[data-tour-id="canonical-dashboard-card"]',
        popover: {
          title: "Dashboard canonico",
          description:
            "Cada card usa una definicion `report_definition` sembrada por tenant.",
        },
      },
      {
        element: '[data-tour-id="dashboard-run-btn"]',
        popover: {
          title: "Ejecutar",
          description:
            "Ejecuta la definicion contra vistas materializadas y persiste `report_execution`.",
        },
      },
      {
        element: '[data-tour-id="dashboard-widget-result"]',
        popover: {
          title: "Resultados",
          description:
            "Los widgets muestran buckets y valores devueltos por el query engine sandboxed.",
        },
      },
      {
        element: '[data-tour-id="dashboard-duplicate-btn"]',
        popover: {
          title: "Duplicar",
          description:
            "Crea una copia editable para personalizar sin tocar el dashboard de sistema.",
        },
      },
      {
        element: '[data-tour-id="report-builder-entry"]',
        popover: {
          title: "Crear dashboard",
          description:
            "Abre el builder visual para armar una nueva definicion.",
        },
      },
    ],
  },
  {
    id: "report-builder-101",
    title: "Crear tu primer report",
    subtitle: "Builder visual y preview",
    category: "Reportes",
    icon: "chart",
    steps: 7,
    durationMin: 3,
    matchPath: /^\/reports\/builder/,
    navigateTo: "/reports/builder",
    build: () => [
      {
        element: '[data-tour-id="report-builder-sources"]',
        popover: {
          title: "Fuentes",
          description:
            "Elige fuentes whitelisted como proyectos, riesgos, workflows, costos IA y usuarios.",
        },
      },
      {
        element: '[data-tour-id="report-builder-source-card"]',
        popover: {
          title: "Agregar widget",
          description:
            "Arrastra una fuente al canvas o haz click para crear un widget.",
        },
      },
      {
        element: '[data-tour-id="report-builder-canvas"]',
        popover: {
          title: "Canvas",
          description:
            "El canvas contiene los widgets que se guardan en `config_jsonb`.",
        },
      },
      {
        element: '[data-tour-id="report-builder-properties"]',
        popover: {
          title: "Propiedades",
          description:
            "Ajusta nombre, codigo, tipo de grafico, metrica, agregacion y group-by.",
        },
      },
      {
        element: '[data-tour-id="report-chart-type"]',
        popover: {
          title: "Tipo de visual",
          description:
            "El builder soporta KPI, barra, linea, pie, donut, tabla, heatmap y funnel.",
        },
      },
      {
        element: '[data-tour-id="report-preview-btn"]',
        popover: {
          title: "Preview",
          description:
            "Ejecuta una configuracion temporal sin crear una definicion permanente.",
        },
      },
      {
        element: '[data-tour-id="report-save-btn"]',
        popover: {
          title: "Guardar",
          description:
            "Persiste el reporte custom como `report_definition` tenant-scoped.",
        },
      },
    ],
  },
  {
    id: "report-schedule",
    title: "Programar reports recurrentes",
    subtitle: "Diario, semanal o mensual",
    category: "Reportes",
    icon: "calendar",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/reports\/.+\/schedules/,
    build: () => [
      fallbackStep(
        "Schedules",
        "`report_schedule` guarda frecuencia, cron, destinatarios, formato y proxima ejecucion.",
      ),
      fallbackStep(
        "Destinatarios",
        "Cada schedule puede enviar PDF, XLSX o CSV a usuarios o emails externos autorizados.",
      ),
      fallbackStep(
        "Entrega",
        "El job ejecuta el reporte, genera export y lo envia por `module-notification`.",
      ),
      fallbackStep(
        "Auditoria",
        "Cada ejecucion programada queda en `report_execution` con trigger `SCHEDULED`.",
      ),
    ],
  },
  {
    id: "report-share",
    title: "Compartir link tokenizado",
    subtitle: "Lectura externa con expiracion",
    category: "Reportes",
    icon: "link",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/reports\/.+/,
    build: () => [
      fallbackStep(
        "Share links",
        "`report_share_link` usa tokens aleatorios, expiracion, password opcional y limite de vistas.",
      ),
      fallbackStep(
        "Read-only",
        "La vista publica no requiere JWT, pero solo lee la definicion con filtros bloqueados.",
      ),
      fallbackStep(
        "Revocar",
        "Un link revocado deja de resolver inmediatamente.",
      ),
      fallbackStep(
        "Trazabilidad",
        "Cada apertura por link se registra como ejecucion `SHARED_LINK`.",
      ),
    ],
  },
  {
    id: "metric-alerts",
    title: "Alertas de metricas",
    subtitle: "Umbrales suscribibles",
    category: "Dashboards",
    icon: "bell",
    steps: 4,
    durationMin: 2,
    matchPath: /^\/dashboards/,
    build: () => [
      {
        element: '[data-tour-id="dashboard-widget-result"]',
        popover: {
          title: "Metricas accionables",
          description:
            "Cada widget puede convertirse en alerta cuando el evaluador de metricas esta activo.",
        },
      },
      fallbackStep(
        "Condicion",
        "`metric_alert` soporta operadores `>`, `<`, `>=`, `<=`, `=` y `CHANGE_PCT`.",
      ),
      fallbackStep(
        "Periodo",
        "Las alertas se evaluan por hora, dia o semana segun `evaluation_period`.",
      ),
      fallbackStep(
        "Notificacion",
        "Cuando dispara, publica evento para S28 inbox, email o push segun preferencias.",
      ),
    ],
  },
  {
    id: "workflow-ai",
    title: "Workflows con IA",
    subtitle: "BPMN y revisión",
    category: "Workflows",
    icon: "workflow",
    steps: 5,
    durationMin: 2,
    matchPath: /^\/workflows/,
    build: () => [
      fallbackStep(
        "Workflows IA",
        "Revisa tareas y automatizaciones asistidas por IA.",
      ),
    ],
  },
];

export function startAiTour(tourId: string, navigate: (path: string) => void) {
  const tour = TOURS.find((item) => item.id === tourId);
  if (!tour) return;
  if (
    tour.navigateTo &&
    !window.location.pathname.startsWith(tour.navigateTo)
  ) {
    navigate(tour.navigateTo);
    window.setTimeout(() => runDriver(tour), 500);
    return;
  }
  runDriver(tour);
}

function runDriver(tour: AiTour) {
  driver({
    showProgress: true,
    smoothScroll: true,
    overlayOpacity: 0.55,
    nextBtnText: "Siguiente",
    prevBtnText: "Anterior",
    doneBtnText: "Listo",
    steps: tour.build().map(safeTourStep),
    onDestroyed: () => {
      const raw = localStorage.getItem("aequia:ai-tours-completed") || "[]";
      const completed = JSON.parse(raw) as string[];
      if (!completed.includes(tour.id)) {
        localStorage.setItem(
          "aequia:ai-tours-completed",
          JSON.stringify([...completed, tour.id]),
        );
      }
    },
  }).drive();
}
